// The modal. Raised surface, dialog radius, the only resting shadow in the
// system. Closes on scrim click and Escape, traps Tab inside, and returns
// focus to where it was. Header takes the title, footer takes the actions
// with the primary on the right. For confirmations and focused edits, not
// for anything a Panel on the page could hold.

import { useEffect, useRef } from 'react'
import './dialog.css'

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

export function Dialog({ title, onClose, footer, width = 480, children }) {
  const boxRef = useRef(null)

  useEffect(() => {
    const previous = document.activeElement
    const box = boxRef.current
    box?.querySelector(FOCUSABLE)?.focus()

    function onKey(event) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab' || !box) return
      const items = [...box.querySelectorAll(FOCUSABLE)].filter((element) => !element.disabled)
      if (!items.length) return
      const first = items[0]
      const last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('keydown', onKey, true)
      previous?.focus?.()
    }
  }, [onClose])

  return (
    <div
      className="ui-scrim"
      onPointerDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="ui-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ width }}
        ref={boxRef}
      >
        <header className="ui-dialog-head">
          <h2>{title}</h2>
        </header>
        <div className="ui-dialog-body">{children}</div>
        {footer ? <footer className="ui-dialog-foot">{footer}</footer> : null}
      </div>
    </div>
  )
}
